import * as React from "react";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Typography from "@mui/material/Typography";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { errorToast, successToast } from "../Constants/Toast";
import { UnprotectedRoute } from "./protectedComonent";
import { setUser } from "../Redux/slices/userSlice";
import { Copyright } from "./signIn";
import { url } from "../Constants/api";

const defaultTheme = createTheme();

export default function PasswordReset() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [step, setStep] = React.useState(1);
  const [email, setEmail] = React.useState("");
  const [emailError, setEmailError] = React.useState(null);
  const [token, setToken] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [tokenError, setTokenError] = React.useState(null);
  const [passwordError, setPasswordError] = React.useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (step === 1) {
      // Reset error states
      setEmailError(null);

      // Validate email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setEmailError("Invalid email address");
        return;
      }

      try {
        // Request password reset
        await axios.post(url.requestPasswordReset, { email });
        successToast("Password reset token sent to your email");
        setStep(2);
      } catch (error) {
        errorToast(error.response ? error.response.data : "Unknown error");
      }
    } else if (step === 2) {
      // Reset error states
      setTokenError(null);
      setPasswordError(null);

      // Validate token and password
      if (token.length !== 5) {
        setTokenError("Invalid token");
        return;
      }

      if (password.length < 8) {
        setPasswordError("Password must be at least 8 characters");
        return;
      }

      try {
        // Reset password
        await axios.post(url.PasswordReset, {
          email,
          token,
          newPassword: password,
        });
        successToast("Password has been reset successfully");
        navigate("/signin");
      } catch (error) {
        errorToast(error.response ? error.response.data : "Unknown error");
      }
    }
  };

  return (
    <UnprotectedRoute>
      <ThemeProvider theme={defaultTheme}>
        <Grid container component="main" sx={{ height: "100vh" }}>
          <CssBaseline />
          <Grid
            item
            xs={false}
            sm={4}
            md={7}
            sx={{
              backgroundImage:
                'url("https://www.sae.edu/gbr/wp-content/uploads/sites/7/2023/12/Future-of-Film-SAE-1-1920x1256.jpg")',
              backgroundColor: (t) =>
                t.palette.mode === "light"
                  ? t.palette.grey[50]
                  : t.palette.grey[900],
              backgroundSize: "cover",
              backgroundPosition: "left",
            }}
          />
          <Grid
            item
            xs={12}
            sm={8}
            md={5}
            component={Paper}
            elevation={6}
            square
          >
            <Box
              sx={{
                my: 8,
                mx: 4,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
                <LockOutlinedIcon />
              </Avatar>
              <Typography component="h1" variant="h5" fontFamily={"Montserrat"}>
                {step === 1 ? "Request Password Reset" : "Reset Password"}
              </Typography>
              <Box
                component="form"
                noValidate
                onSubmit={handleSubmit}
                sx={{ mt: 1 }}
              >
                {step === 1 && (
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="email"
                    label="Email Address"
                    name="email"
                    autoComplete="email"
                    autoFocus
                    error={!!emailError}
                    helperText={emailError}
                    onChange={(e) => setEmail(e.target.value)}
                    value={email}
                  />
                )}

                {step === 2 && (
                  <>
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      id="token"
                      label="Token"
                      name="token"
                      autoComplete="token"
                      error={!!tokenError}
                      helperText={tokenError}
                      onChange={(e) => setToken(e.target.value)}
                      value={token}
                    />
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      name="password"
                      label="New Password"
                      type="password"
                      id="password"
                      autoComplete="current-password"
                      error={!!passwordError}
                      helperText={passwordError}
                      onChange={(e) => setPassword(e.target.value)}
                      value={password}
                    />
                  </>
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                >
                  {step === 1 ? "Send Token" : "Reset Password"}
                </Button>
                <Grid container>
                  <Grid item xs>
                    <Link
                      onClick={(e) => {
                        e.preventDefault();
                        navigate("/signin");
                      }}
                      href="/signin"
                      variant="body2"
                    >
                      Back to Sign In
                    </Link>
                  </Grid>
                </Grid>
                <Copyright sx={{ mt: 5 }} />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </ThemeProvider>
    </UnprotectedRoute>
  );
}
